/**
 * ToolkitDownload — lite-assessment lead magnet. Generates a tailored,
 * downloadable EKSELENS360 discovery toolkit (benefits map + targeted
 * discovery questions + operating-system next steps) from the visitor's
 * lite result. Fire-and-poll so the Opus call never trips a proxy timeout.
 */
import React, { useCallback, useRef, useState } from "react";
import { FileDown, Loader2, CheckCircle2, ArrowRight, Mail } from "lucide-react";
import { API_BASE } from "../../lib/api";

export default function ToolkitDownload({ slug, answers, subBrand }) {
  const [status, setStatus] = useState("idle"); // idle | generating | ready | failed
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(true);
  const [emailState, setEmailState] = useState("idle"); // idle | sending | sent
  const tokenRef = useRef(null);

  const download = useCallback((token) => {
    window.open(`${API_BASE}/public/assessments/lite/toolkit/${token}/download.pdf`, "_blank", "noopener");
  }, []);

  const poll = useCallback((token) => {
    let tries = 0;
    const id = setInterval(async () => {
      tries += 1;
      try {
        const res = await fetch(`${API_BASE}/public/assessments/lite/toolkit/${token}`);
        const data = await res.json();
        if (data.status === "ready") {
          clearInterval(id);
          setStatus("ready");
          download(token);
        } else if (data.status === "failed" || tries > 30) {
          clearInterval(id);
          setStatus("failed");
        }
      } catch {
        if (tries > 30) { clearInterval(id); setStatus("failed"); }
      }
    }, 4000);
  }, [download]);

  const generate = useCallback(async () => {
    setStatus("generating");
    try {
      const res = await fetch(`${API_BASE}/public/assessments/lite/${slug}/toolkit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ answers }),
      });
      if (!res.ok) { setStatus("failed"); return; }
      const data = await res.json();
      tokenRef.current = data.token;
      poll(data.token);
    } catch {
      setStatus("failed");
    }
  }, [slug, answers, poll]);

  const sendEmail = useCallback(async () => {
    if (!email || !consent || !tokenRef.current) return;
    setEmailState("sending");
    try {
      const res = await fetch(`${API_BASE}/public/assessments/lite/toolkit/${tokenRef.current}/email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, consent }),
      });
      setEmailState(res.ok ? "sent" : "idle");
    } catch {
      setEmailState("idle");
    }
  }, [email, consent]);

  return (
    <div className="mt-10 border border-[#7737F6]/30 bg-[#7737F6]/[0.04] p-6 md:p-8" data-testid="lite-toolkit-block">
      <div className="flex flex-col md:flex-row md:items-center gap-5 md:gap-8">
        <div className="flex-1">
          <div className="text-[11px] font-medium tracking-[0.18em] text-[#7737F6] uppercase mb-2">Your tailored toolkit</div>
          <h3 className="ek-heading text-xl md:text-2xl text-black">Take your result further — free</h3>
          <p className="text-zinc-600 text-[14px] mt-3 max-w-2xl">
            A discovery toolkit tailored to <strong>your</strong> {subBrand} scores: a benefits map (why each change is worth doing and how it's measured), sharp discovery questions to take into your next leadership conversation, and your EKSELENS360 next steps. Built to put in front of a decision-maker.
          </p>
        </div>
        <div className="md:w-56 shrink-0">
          {status === "idle" && (
            <button type="button" onClick={generate} className="ek-btn ek-btn-primary w-full justify-center inline-flex items-center gap-2" data-testid="lite-toolkit-generate">
              <FileDown size={15} /> Build my toolkit
            </button>
          )}
          {status === "generating" && (
            <div className="text-[13px] text-zinc-600 flex items-center gap-2" data-testid="lite-toolkit-generating">
              <Loader2 size={16} className="animate-spin text-[#7737F6]" /> Tailoring to your result…
            </div>
          )}
          {status === "ready" && (
            <div data-testid="lite-toolkit-ready">
              <div className="text-[13px] text-emerald-700 flex items-center gap-1.5 mb-2">
                <CheckCircle2 size={15} /> Ready — opened in a new tab
              </div>
              <button type="button" onClick={() => download(tokenRef.current)} className="text-[#7737F6] hover:underline text-[13px] inline-flex items-center gap-1" data-testid="lite-toolkit-redownload">
                Download again <ArrowRight size={13} />
              </button>
            </div>
          )}
          {status === "failed" && (
            <div data-testid="lite-toolkit-failed">
              <div className="text-[13px] text-amber-700 mb-2">Couldn't build it just now.</div>
              <button type="button" onClick={generate} className="text-[#7737F6] hover:underline text-[13px]" data-testid="lite-toolkit-retry">Try again</button>
            </div>
          )}
        </div>
      </div>

      {status === "ready" && (
        <div className="mt-5 pt-5 border-t border-[#7737F6]/15" data-testid="lite-toolkit-email-block">
          {emailState === "sent" ? (
            <div className="text-[13px] text-emerald-700 flex items-center gap-1.5" data-testid="lite-toolkit-email-sent">
              <CheckCircle2 size={15} /> On its way to {email}.
            </div>
          ) : (
            <div>
              <div className="text-[13px] text-zinc-600 mb-2 flex items-center gap-1.5">
                <Mail size={14} className="text-[#7737F6]" /> Want a copy emailed too? <span className="text-zinc-400">(optional — you've already got the download)</span>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 sm:items-center max-w-xl">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@organisation.com"
                  className="flex-1 border border-zinc-300 px-3 py-2 text-[13px] focus:border-[#7737F6] focus:outline-none"
                  data-testid="lite-toolkit-email-input"
                />
                <button
                  type="button"
                  onClick={sendEmail}
                  disabled={!email || !consent || emailState === "sending"}
                  className="ek-btn ek-btn-primary text-[12.5px] justify-center inline-flex items-center gap-1.5 disabled:opacity-40 whitespace-nowrap"
                  data-testid="lite-toolkit-email-send"
                >
                  {emailState === "sending" ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} Email me a copy
                </button>
              </div>
              <label className="flex items-start gap-2 mt-2 text-[11.5px] text-zinc-500 max-w-xl cursor-pointer">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5" data-testid="lite-toolkit-email-consent" />
                I'm happy for Ekselens to email this toolkit and follow up about the full {subBrand} diagnostic. We store your email for this purpose only.
              </label>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
